import { useSyncExternalStore } from 'react';
import { alvo, elements } from '../code/parse';
import { removeElemento } from '../code/edit';
import { ehMotor, MOTOR_PADRAO, type Motor } from '../code/motor';
import { aplicaAltas, type Achado } from '../ai/review';
import { problemasDoBloco, type ProblemaBloco } from '../code/regras';
import { corrigeImports } from '../code/imports';
import type { CorMedida } from '../code/referencia';
import type { Referencia } from '../ai/esquema';

export interface State {
	/** o código do bloco — a fonte da verdade. O AST é derivado, nunca guardado. */
	source: string;
	/** "início-fim" da tag selecionada, o mesmo valor que viaja no data-cb-loc */
	selected: string | null;
	generating: boolean;
	error: string | null;
	/** achados da revisão de animação; null = nunca revisado */
	review: Achado[] | null;
	reviewing: boolean;
	/** imagem de referência (data URL) enquanto ela espera para ser gerada */
	image: string | null;
	/** cores medidas dela — o modelo estima cor mal, então vão junto no pedido */
	palette: CorMedida[];
	/** o que quebra as regras do bloco: prancheta fixa e navegação */
	problemas: ProblemaBloco[];
	/** a medição da referência, já validada; null fora do modo imagem */
	referencia: Referencia | null;
	/** a leitura da imagem roda antes da geração e leva ~20s */
	lendo: boolean;
	/** o iframe confirmou que montou o bloco; é o que separa "compilando" de "pronto" */
	montado: boolean;
	/** qual backend gera — os dois gêmeos rodam juntos (../code/motor) */
	motor: Motor;
	alvo: string | null;
	/** o bloco voltou quebrado e está indo para uma passada de conserto */
	consertando: boolean;
	/** o bloco montou e explodiu logo em seguida — o que o conserto de runtime lê */
	falhaRuntime: string | null;
	consertos: number;
	/** quem sabe enviar arquivo é o app (endpoint, id do bloco); sem isso a troca de foto some */
	enviarImagem: EnviaImagem | null;
}

export type EnviaImagem = (file: File) => Promise<string | null>;

const CHAVE_MOTOR = 'cb:motor';

function motorSalvo(): Motor {
	try {
		const salvo = localStorage.getItem(CHAVE_MOTOR);
		return ehMotor(salvo) ? salvo : MOTOR_PADRAO;
	} catch {
		return MOTOR_PADRAO; // storage bloqueado não é motivo para não abrir o editor
	}
}

let state: State = {
	source: '',
	selected: null,
	generating: false,
	error: null,
	review: null,
	reviewing: false,
	image: null,
	palette: [],
	problemas: [],
	referencia: null,
	lendo: false,
	montado: false,
	motor: motorSalvo(),
	alvo: null,
	consertando: false,
	falhaRuntime: null,
	consertos: 0,
	enviarImagem: null,
};
const listeners = new Set<() => void>();

function set(patch: Partial<State>): void {
	state = { ...state, ...patch };
	listeners.forEach((l) => l());
}

/**
 * O app injeta o envio de arquivo uma vez, no boot. Fica fora do `actions` porque não é uma edição
 * do bloco: é a lib recebendo uma capacidade que só o app tem.
 */
export function definirEnvioDeImagem(enviarImagem: EnviaImagem | null): void {
	set({ enviarImagem });
}

/** O estado fora do React — é o que a orquestração lê sem virar dependência de render. */
export function estado(): State {
	return state;
}

export function useStore<T>(selector: (s: State) => T): T {
	return useSyncExternalStore(
		(cb) => {
			listeners.add(cb);
			return () => listeners.delete(cb);
		},
		() => selector(state),
		() => selector(state),
	);
}

function reselect(source: string, selected: string | null): string | null {
	if (!selected) return null;
	const { loc, index } = alvo(selected);
	const start = Number(loc.split('-')[0]);
	try {
		const achado = elements(source).find((e) => e.start === start)?.loc;
		// a instância não muda com a edição: quem está no slide 2 continua no slide 2
		return achado ? (index === null ? achado : `${achado}#${index}`) : null;
	} catch {
		return null;
	}
}

export const actions = {
	/** Bloco novo: some a referência e as regras são verificadas de novo. */
	setSource(entrada: string) {
		// o modelo esquece o import do ícone o tempo todo; conserta antes de guardar
		const source = corrigeImports(entrada);
		set({
			source,
			selected: null,
			error: null,
			review: null,
			image: null,
			palette: [],
			problemas: problemasDoBloco(source),
			// bloco novo ainda não montou: a etapa é "compilando" até a ponte responder
			montado: false,
			// geração nova zera o orçamento de conserto
			consertando: false,
			falhaRuntime: null,
			consertos: 0,
		});
	},
	setConsertando(consertando: boolean) {
		set({ consertando, error: consertando ? null : state.error });
	},
	setFalhaRuntime(falhaRuntime: string | null) {
		if (falhaRuntime !== state.falhaRuntime) set({ falhaRuntime });
	},
	aplicaConserto(entrada: string) {
		const source = corrigeImports(entrada);
		set({
			source,
			selected: null,
			error: null,
			montado: false,
			consertando: false,
			falhaRuntime: null,
			consertos: state.consertos + 1,
			problemas: problemasDoBloco(source),
		});
	},
	setMontado(montado: boolean) {
		if (montado !== state.montado) set({ montado });
	},
	/** O bloco atual FICA: trocar de motor é para gerar de novo e comparar. */
	setMotor(motor: Motor) {
		if (motor === state.motor) return;
		try {
			localStorage.setItem(CHAVE_MOTOR, motor);
		} catch {
			/* sem storage o motor vale só nesta aba, e isso já serve */
		}
		set({ motor, error: null });
	},
	/** Referência nova invalida a medição da anterior. */
	setImage(image: string | null, palette: State['palette'] = []) {
		set({ image, palette, error: null, referencia: null });
	},
	setLendo(lendo: boolean) {
		set({ lendo, error: lendo ? null : state.error });
	},
	/** A medição sobrevive à geração: é dela que sai o recorte de cada foto. */
	setReferencia(referencia: Referencia | null) {
		set({ referencia, lendo: false });
	},
	/** edição: recebe a função para nunca operar em cima de um source velho */
	apply(fn: (src: string) => string) {
		const next = fn(state.source);
		if (next === state.source) return;
		// editar no Inspector pode tirar o tamanho da raiz: reconfere a prancheta
		set({ source: next, selected: reselect(next, state.selected), error: null, problemas: problemasDoBloco(next) });
	},
	select(loc: string | null) {
		if (loc !== state.selected) set({ selected: loc });
	},
	remove(sel: string): boolean {
		const source = removeElemento(state.source, sel);
		if (source === null) return false;
		set({ source, selected: null, error: null, problemas: problemasDoBloco(source) });
		return true;
	},
	setGenerating(generating: boolean) {
		set({ generating, error: generating ? null : state.error });
	},
	setAlvo(alvo: string | null) {
		if (alvo !== state.alvo) set({ alvo });
	},
	setError(error: string | null) {
		set({ error, generating: false, reviewing: false, lendo: false, alvo: error ? null : state.alvo });
	},
	setReviewing(reviewing: boolean) {
		set({ reviewing, error: reviewing ? null : state.error });
	},
	setReview(review: Achado[] | null) {
		set({ review, reviewing: false });
	},
	applyReview(achados: Achado[]) {
		const { source, achados: marcados } = aplicaAltas(state.source, achados);
		set({
			source,
			selected: source === state.source ? state.selected : reselect(source, state.selected),
			review: marcados,
			reviewing: false,
		});
	},
};
